import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuthContext } from '../contexts/AuthContext';
import { useUrlContext } from '../contexts/UrlContext';
import { DocumentsInfo } from '../types';
import toastConfig from '../utils/constants/toastConfig';
import formatNBTVA from '../utils/functions/formatNBTVA';
import {
	getProDocumentUploadAccept,
	isAllowedProDocumentUploadFile,
	ProDocumentUploadKey,
} from '../utils/files/proDocumentUploadRules';
import { emitAnalytics, getTelemetryDeviceType } from '../services/telemetry/appInsights';

type WrongFields = {
	companyRegistration: boolean;
	identityCard: boolean;
};

type DocumentKey = keyof Pick<
	DocumentsInfo,
	'companyRegistration' | 'identityCard' | 'identityCardBack' | 'other'
>;

const SINGLE_FILE_FIELDS: DocumentKey[] = [
	'companyRegistration',
	'identityCard',
	'identityCardBack',
];

const useProSignupDocumentPanel = () => {
	const {
		userInfo,
		setUserInfo,
		isThirdPanelValidated,
		loadingSignup,
		shouldSkipDocumentUploadStep,
		validateThirdPanel,
		openedPanel,
		setOpenedPanel,
	} = useAuthContext();
	const { redirectToContactUsPro } = useUrlContext();
	const { t, i18n } = useTranslation();
	const [wrongFields, setWrongFields] = useState<WrongFields>({
		companyRegistration: false,
		identityCard: false,
	});
	const [isMissingBackDialogOpen, setIsMissingBackDialogOpen] = useState(false);

	const sectionTitle = shouldSkipDocumentUploadStep
		? t('thirdPanel:dead_end_section_title')
		: t('thirdPanel:documents_section');
	const sectionSubtitle = shouldSkipDocumentUploadStep
		? ''
		: t('thirdPanel:documents_subtitle');
	const warningVatNumber = formatNBTVA(userInfo?.company?.nbtva ?? '');
	const warningCompanyName = userInfo?.company?.companyName ?? '';

	const handleFilesUpload = async (type: DocumentKey, files: FileList): Promise<boolean> => {
		const documentType = type as ProDocumentUploadKey;
		const validFiles = Array.from(files).filter((file) => {
			if (file.size > 2 * 1024 * 1024) {
				toast.warn(t('thirdPanel:file_size_warning'), toastConfig);
				return false;
			}
			return true;
		});

		const allowedFiles = validFiles.filter((file) => {
			if (isAllowedProDocumentUploadFile(file, documentType)) {
				return true;
			}

			return false;
		});

		if (allowedFiles.length !== validFiles.length) {
			toast.warn(t('thirdPanel:unsupported_file_type_warning'), toastConfig);
		}

		const filePromises = allowedFiles.map((file) => {
			return new Promise<{ name: string; data: string }>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					resolve({ name: file.name, data: reader.result as string });
				};
				reader.onerror = reject;
				reader.readAsDataURL(file);
			});
		});

			try {
				const fileObjects = await Promise.all(filePromises);
					if (fileObjects.length === 0) return false;

					fileObjects.forEach((file) => {
						emitAnalytics('pro_signup_document_uploaded', {
							entryRoute: '/pro/signup',
							documentType: documentType,
							fileExtension: file.name.split('.').pop()?.toLowerCase(),
							hasMultipleFiles: fileObjects.length > 1,
							deviceType: getTelemetryDeviceType(),
							language: i18n.language,
						});
					});

					setUserInfo((prevUserInfo: any) => {
					const updatedFiles = SINGLE_FILE_FIELDS.includes(type)
						? fileObjects.slice(0, 1)
						: [...prevUserInfo.documents[type], ...fileObjects];

					return {
						...prevUserInfo,
						documents: {
							...prevUserInfo.documents,
							[type]: updatedFiles,
						},
					};
				});

				if (type === 'companyRegistration' || type === 'identityCard') {
					setWrongFields((prev) => ({ ...prev, [type]: false }));
				}
				return true;
			} catch (error) {
				console.error('Erreur lors du chargement des fichiers :', error);
				toast.error(t('thirdPanel:file_upload_error'), toastConfig);
				return false;
			}
	};

	const handleRemoveFile = (type: DocumentKey, index: number) => {
		setUserInfo((prevUserInfo: any) => {
			const updatedFiles = [...prevUserInfo.documents[type]];
			updatedFiles.splice(index, 1);
			return {
				...prevUserInfo,
				documents: {
					...prevUserInfo.documents,
					[type]: updatedFiles,
				},
			};
		});
	};

	const validateFields = () => {
		const newWrongFields = {
			companyRegistration: userInfo.documents.companyRegistration.length === 0,
			identityCard: userInfo.documents.identityCard.length === 0,
		};
		setWrongFields(newWrongFields);

		return !newWrongFields.companyRegistration && !newWrongFields.identityCard;
	};

	const handleValidate = () => {
		if (shouldSkipDocumentUploadStep) {
			redirectToContactUsPro();
			return;
		}

		if (!validateFields()) return;

			if (userInfo.documents.identityCardBack.length === 0) {
				emitAnalytics('pro_signup_missing_identity_back_dialog_opened', {
					entryRoute: '/pro/signup',
					documentType: 'identityCardBack',
					countryCode: userInfo?.company?.countryCode,
					deviceType: getTelemetryDeviceType(),
					language: i18n.language,
				});
				setIsMissingBackDialogOpen(true);
				return;
			}

		validateThirdPanel();
	};

	const handleMissingBackConfirm = () => {
		setIsMissingBackDialogOpen(false);
		validateThirdPanel();
	};

	const handleMissingBackCancel = () => {
		setIsMissingBackDialogOpen(false);
	};

	return {
		userInfo,
		isThirdPanelValidated,
		loadingSignup,
		shouldSkipDocumentUploadStep,
		openedPanel,
		setOpenedPanel,
		sectionTitle,
		sectionSubtitle,
		warningVatNumber,
		warningCompanyName,
		wrongFields,
		isMissingBackDialogOpen,
		handleFilesUpload,
		handleRemoveFile,
		handleValidate,
		handleMissingBackConfirm,
		handleMissingBackCancel,
		getProDocumentUploadAccept,
	};
};

export default useProSignupDocumentPanel;
